<template>
  <div>
    <component
      :is="resolveLayoutVariant"
      :class="`skin-variant--${appSkinVariant}`"
    >
      <transition
        :name="appRouteTransition"
        mode="out-in"
        appear
      >
        <router-view></router-view>
      </transition>
    </component>
    <WatermarkDev v-if="showWatermark" />

    <transition name="onboarding-fade">
      <div
        v-if="showWelcomeOverlay"
        class="onboarding-overlay"
      >
        <div class="onboarding-orb onboarding-orb--one"></div>
        <div class="onboarding-orb onboarding-orb--two"></div>

        <div class="onboarding-card animate__animated animate__zoomInUp">
          <div class="onboarding-emoji">🚀</div>
          <p class="onboarding-kicker">Primeiro acesso</p>
          <h1 class="onboarding-title">Bem-vindo!</h1>
          <p class="onboarding-text">
            Vamos te mostrar os principais recursos do sistema para você começar com o pé direito.
          </p>

          <div class="onboarding-highlights">
            <div class="onboarding-highlight">
              <div class="onboarding-highlight-icon">📋</div>
              <strong>Menu</strong>
              <span>módulos e cadastros principais</span>
            </div>
            <div class="onboarding-highlight">
              <div class="onboarding-highlight-icon">🔍</div>
              <strong>Busca</strong>
              <span>localize registros e páginas rapidamente</span>
            </div>
            <div class="onboarding-highlight">
              <div class="onboarding-highlight-icon">📊</div>
              <strong>Dashboard</strong>
              <span>acompanhe estoque, caixa e resultados</span>
            </div>
          </div>

          <div class="onboarding-actions">
            <v-btn
              text
              class="px-4 onboarding-skip-btn"
              @click="skipOnboarding"
            >
              Agora não
            </v-btn>
            <v-btn
              color="info"
              class="px-5 onboarding-tour-btn"
              @click="startWelcomeTour"
            >
              Começar tour
            </v-btn>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { computed, nextTick, onMounted, ref, watch } from '@vue/composition-api'
import { driver } from 'driver.js'
// eslint-disable-next-line import/no-unresolved
import useAppConfig from '@core/@app-config/useAppConfig'
import { useRouter } from '@core/utils'
import { useLayout } from '@core/layouts/composable/useLayout'

// Layouts
import LayoutContentVerticalNav from '@/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue'
import LayoutContentHorizontalNav from '@/layouts/variants/content/horizontal-nav/LayoutContentHorizontalNav.vue'
import LayoutManager from '@/layouts/variants/manager/LayoutManager.vue'
import LayoutBlank from '@/layouts/variants/blank/LayoutBlank.vue'
import WatermarkDev from '@/components/WatermarkDev.vue'

// Dynamic vh
import useDynamicVh from '@core/utils/useDynamicVh'
import store from '@/store'

export default {
  components: {
    LayoutContentVerticalNav,
    LayoutContentHorizontalNav,
    LayoutManager,
    LayoutBlank,
    WatermarkDev,
  },
  setup() {
    const { route } = useRouter()
    const { appContentLayoutNav, appSkinVariant, appRouteTransition } = useAppConfig()
    const showWelcomeOverlay = ref(false)
    const onboardingTour = ref(null)
    const pendingWelcomeTour = ref(false)

    const { handleBreakpointLayoutSwitch } = useLayout()
    handleBreakpointLayoutSwitch()

    const hasSeenOnboarding = () => {
      return store.getters['Login/getOnboardingDone']
    }

    const markOnboardingAsSeen = () => {
      store.dispatch('Login/markOnboardingDone')
    }

    const createOnboardingTour = () =>
      driver({
        animate: true,
        smoothScroll: true,
        allowClose: true,
        overlayOpacity: 0.58,
        stagePadding: 8,
        popoverClass: 'spa-onboarding-popover',
        showProgress: true,
        progressText: '{{current}} de {{total}}',
        nextBtnText: 'Próximo →',
        prevBtnText: '← Voltar',
        doneBtnText: 'Finalizar',
        onPopoverRender: popover => {
          popover.wrapper.style.zIndex = '1000002'
          popover.wrapper.style.overflow = 'auto'
          requestAnimationFrame(() => {
            if (popover.config.element === '[data-onboarding="dashboard-expense-overdue"]') {
              popover.wrapper.style.top = '320px'
            } else {
              popover.wrapper.style.top = '110px'
            }
            popover.wrapper.style.right = '24px'
            popover.wrapper.style.left = 'auto'
            popover.wrapper.style.bottom = 'auto'
          })
        },
        steps: [
          {
            element: '[data-onboarding="dashboard-stock"]',
            popover: {
              title: 'Visão geral do estoque',
              description:
                'Este card mostra o valor total do estoque e, ao clicar nele, você pode visualizar todos os produtos disponíveis.',
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-onboarding="dashboard-cash"]',
            popover: {
              title: 'Caixa e financeiro',
              description: 'Aqui você acompanha a composição do caixa e os indicadores financeiros mais usados.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '[data-onboarding="dashboard-revenue-overdue"]',
            popover: {
              title: 'Receita em atraso',
              description: 'Acompanhe os valores de receitas que estão vencidas e precisam de atenção.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '[data-onboarding="dashboard-expense-overdue"]',
            popover: {
              title: 'Despesa em atraso',
              description: 'Veja as despesas vencidas e mantenha o controle financeiro em dia.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-onboarding="top-search"]',
            popover: {
              title: 'Busca rápida',
              description: 'Pesquise clientes, produtos, páginas e atalhos sem sair da tela atual.',
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-onboarding="help-button"]',
            popover: {
              title: 'Ajuda e Suporte',
              description: 'Tire dúvidas, acesse a central de ajuda e suporte e entenda melhor cada funcionalidade do sistema.',
              side: 'right',
              align: 'start',
            },
          },
        ],
      })

    const runWelcomeTour = () => {
      if (!pendingWelcomeTour.value) return

      pendingWelcomeTour.value = false
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          onboardingTour.value = createOnboardingTour()
          onboardingTour.value.drive()
        })
      })
    }

    const startWelcomeTour = async () => {
      markOnboardingAsSeen()
      pendingWelcomeTour.value = true
      showWelcomeOverlay.value = false

      await nextTick()
    }

    const skipOnboarding = () => {
      markOnboardingAsSeen()
      showWelcomeOverlay.value = false
    }

    const restartTour = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const tour = createOnboardingTour()
          tour.drive()
        })
      })
    }

    const shouldShowOnboarding = () => route.value.name === 'dashboard' && !hasSeenOnboarding()

    const openOnboardingIfNeeded = () => {
      showWelcomeOverlay.value = shouldShowOnboarding()
    }

    onMounted(() => {
      openOnboardingIfNeeded()
    })

    window.restartTour = restartTour

    watch(
      () => route.value.name,
      () => {
        openOnboardingIfNeeded()
      },
    )

    watch(
      () => showWelcomeOverlay.value,
      value => {
        if (!value) runWelcomeTour()
      },
    )

    const resolveLayoutVariant = computed(() => {
      if (route.value.meta.layout === 'blank') return 'layout-blank'
      if (route.value.meta.layout === 'manager') return 'layout-manager'
      if (route.value.meta.layout === 'content') return `layout-content-${appContentLayoutNav.value}-nav`

      return null
    })

    useDynamicVh()

    // Detecta ambiente
    const env = process.env.NODE_ENV || ''
    const showWatermark = env === 'development' || env === 'homolog' || env === 'homologacao'

    return {
      resolveLayoutVariant,
      appSkinVariant,
      appRouteTransition,
      showWatermark,
      showWelcomeOverlay,
      startWelcomeTour,
      skipOnboarding,
      runWelcomeTour,
    }
  },
}
</script>

<style>
* {
  font-size: 14px;
}

.v-application input,
.v-application textarea,
.v-application .v-select .v-select__selection,
.v-application .v-select .v-label {
  font-size: medium !important;
  color: #0d1619 !important;
}

/* //tema dark */
.theme--dark input,
.theme--dark textarea,
.theme--dark .v-select .v-select__selection,
.theme--dark .v-select .v-label {
  font-size: medium !important;
  color: #ffffff !important;
}

.onboarding-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: radial-gradient(circle at top left, rgba(59, 130, 246, 0.25), transparent 40%),
    radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.2), transparent 35%),
    linear-gradient(145deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.95));
  backdrop-filter: blur(12px);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.onboarding-card {
  position: relative;
  width: min(720px, 100%);
  overflow: hidden;
  padding: 40px;
  border-radius: 30px;
  background: linear-gradient(180deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.5);
  color: #f8fafc;
}

.driver-popover.spa-onboarding-popover {
  z-index: 1000002 !important;
  position: fixed !important;
  top: 110px;
  right: 24px;
  left: auto;
  bottom: auto;
  width: min(620px, calc(100vw - 48px)) !important;
  max-width: 620px !important;
  max-height: calc(100vh - 160px) !important;
  overflow: auto !important;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(17, 24, 39, 0.96));
  color: #f8fafc;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
  padding: 16px 20px !important;
  border-radius: 14px !important;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
  font-size: 17px !important;
  line-height: 1.6 !important;
}

.driver-popover.spa-onboarding-popover * {
  box-sizing: border-box;
}

.driver-popover.spa-onboarding-popover .driver-popover-title,
.driver-popover.spa-onboarding-popover .driver-popover-description,
.driver-popover.spa-onboarding-popover .driver-popover-progress-text {
  color: #f8fafc;
}

.driver-popover.spa-onboarding-popover .driver-popover-title {
  margin: 0 0 12px;
  font-size: 22px !important;
  font-weight: 800 !important;
  line-height: 1.2 !important;
  letter-spacing: -0.02em;
}

.driver-popover.spa-onboarding-popover .driver-popover-description {
  margin: 0 0 16px;
  font-size: 17px !important;
  line-height: 1.75 !important;
  white-space: normal !important;
}

.driver-popover.spa-onboarding-popover .driver-popover-footer {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  gap: 10px !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  justify-content: space-between !important;
}

.driver-popover.spa-onboarding-popover .driver-popover-progress-text {
  margin-right: 0;
  flex: 0 0 auto;
  font-size: 14px !important;
  font-weight: 700;
  letter-spacing: 0.02em;
  opacity: 0.9;
}

.driver-popover.spa-onboarding-popover .driver-popover-navigation-btns {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  gap: 10px;
  margin-left: auto;
}

.driver-popover.spa-onboarding-popover .driver-popover-prev-btn,
.driver-popover.spa-onboarding-popover .driver-popover-close-btn {
  color: #e2e8f0 !important;
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
  padding: 12px 16px !important;
  border-radius: 14px !important;
  box-shadow: none !important;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
  min-width: 104px;
}

.driver-popover.spa-onboarding-popover .driver-popover-close-btn {
  position: absolute !important;
  top: 14px !important;
  right: 14px !important;
  left: auto !important;
  min-width: 0 !important;
  width: 34px !important;
  height: 34px !important;
  padding: 0 !important;
  border-radius: 999px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 2;
}

.driver-popover.spa-onboarding-popover .driver-popover-next-btn {
  color: #ffffff !important;
  background: linear-gradient(135deg, #1d4ed8, #3b82f6) !important;
  border: 1px solid rgba(96, 165, 250, 0.65) !important;
  font-size: 15px !important;
  font-weight: 800 !important;
  line-height: 1.2 !important;
  padding: 12px 18px !important;
  border-radius: 14px !important;
  box-shadow: 0 12px 28px rgba(37, 99, 235, 0.32) !important;
  transition: transform 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease;
  min-width: 118px;
}

.driver-popover.spa-onboarding-popover .driver-popover-prev-btn:hover,
.driver-popover.spa-onboarding-popover .driver-popover-close-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
  transform: translateY(-1px);
}

.driver-popover.spa-onboarding-popover .driver-popover-next-btn:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
  box-shadow: 0 16px 32px rgba(37, 99, 235, 0.4) !important;
}

.driver-popover.spa-onboarding-popover .driver-popover-arrow-side-top.driver-popover-arrow {
  border-top-color: #0f172a;
}

.driver-popover.spa-onboarding-popover .driver-popover-arrow-side-bottom.driver-popover-arrow {
  border-bottom-color: #0f172a;
}

.driver-popover.spa-onboarding-popover .driver-popover-arrow-side-left.driver-popover-arrow {
  border-left-color: #0f172a;
}

.driver-popover.spa-onboarding-popover .driver-popover-arrow-side-right.driver-popover-arrow {
  border-right-color: #0f172a;
}

.onboarding-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent 35%);
  pointer-events: none;
}

.onboarding-kicker {
  display: inline-flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.18);
  color: #bfdbfe;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.onboarding-title {
  margin: 0;
  font-size: clamp(2.25rem, 4.7vw, 3.6rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
}

.onboarding-text {
  max-width: 54ch;
  margin: 16px 0 0;
  font-size: 1.12rem;
  line-height: 1.8;
  color: rgba(226, 232, 240, 0.86);
}

.onboarding-highlights {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 24px;
}

.onboarding-highlight {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: background 0.2s;
}

.onboarding-highlight:hover {
  background: rgba(255, 255, 255, 0.09);
}

.onboarding-highlight-icon {
  font-size: 1.5rem;
  margin-bottom: 6px;
}

.onboarding-highlight strong {
  font-size: 1.02rem;
  color: #ffffff;
}

.onboarding-highlight span {
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(226, 232, 240, 0.75);
}

.onboarding-emoji {
  font-size: 3.2rem;
  margin-bottom: 8px;
  line-height: 1;
}

.onboarding-actions .v-btn {
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
}

.onboarding-skip-btn {
  color: rgba(226, 232, 240, 0.7) !important;
}

.onboarding-skip-btn:hover {
  color: #f8fafc !important;
}

.onboarding-tour-btn {
  background: linear-gradient(135deg, #1d4ed8, #3b82f6) !important;
  color: #ffffff !important;
  border: 1px solid rgba(96, 165, 250, 0.6) !important;
  box-shadow: 0 12px 28px rgba(37, 99, 235, 0.3);
}

.onboarding-tour-btn:hover {
  background: linear-gradient(135deg, #1e40af, #2563eb) !important;
  color: #ffffff !important;
}

.onboarding-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
}

.onboarding-orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(2px);
  opacity: 0.7;
  animation: onboarding-float 8s ease-in-out infinite;
}

.onboarding-orb--one {
  top: 10%;
  right: 10%;
  width: 140px;
  height: 140px;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.5), rgba(59, 130, 246, 0.08));
}

.onboarding-orb--two {
  bottom: 12%;
  left: 8%;
  width: 90px;
  height: 90px;
  background: radial-gradient(circle, rgba(34, 197, 94, 0.38), rgba(34, 197, 94, 0.05));
  animation-delay: -2.5s;
}

.onboarding-fade-enter-active,
.onboarding-fade-leave-active {
  transition: opacity 0.25s ease;
}

.onboarding-fade-enter,
.onboarding-fade-leave-to {
  opacity: 0;
}

@keyframes onboarding-float {
  0%,
  100% {
    transform: translateY(0px) scale(1);
  }
  50% {
    transform: translateY(-14px) scale(1.04);
  }
}

@media (max-width: 700px) {
  .onboarding-card {
    padding: 24px;
  }

  .onboarding-highlights {
    grid-template-columns: 1fr;
  }

  .onboarding-actions {
    flex-direction: column-reverse;
  }

  .onboarding-actions .v-btn {
    width: 100%;
  }
}
</style>
